
// Modal window shown from the buy order ADD button ("From warehouse orders"): lists the pending warehouse
// orders of the venue so the user picks which ones to purchase, plus an on/off switch to also add the
// products below their minimum stock that none of the picked orders already asks for. Tapping an order
// pushes its lines; the circle on the left selects it.
class BuyOrderWarehouseOrdersViewController extends MUIViewController
{
    static newInstance() : BuyOrderWarehouseOrdersViewController {
        let vc = new BuyOrderWarehouseOrdersViewController("buy-order-warehouse-orders-view");
        vc.initWithResource("layout/stock_buy_orders/BuyOrderWarehouseOrdersView.html");
        return vc;
    }

    get preferredContentSize(): MIOSize {
        return new MIOSize(700, 520);
    }

    private closeButton:MUIButton = null;
    private doneButton:MUIButton = null;
    private tableView:UITableView = null;

    private completionTarget = null;
    private completionBlock = null;

    private includeMinStock = false;
    private selectedOrders = new MIOSet();

    setCompletionBlock(target, completion:(warehouseOrders:WarehouseOrder[], includeMinStock:boolean) => void){
        this.completionTarget = target;
        this.completionBlock = completion;
    }

    viewDidLoad(){
        super.viewDidLoad();

        this.closeButton = MUIOutlet(this, "close-btn", "MUIButton");
        this.closeButton.setAction(this, function(this:BuyOrderWarehouseOrdersViewController){
            this.dismissViewController(true);
        });

        this.doneButton = MUIOutlet(this, "done-btn", "MUIButton");
        this.doneButton.setAction(this, this.doneAction);

        this.tableView = MUIOutlet(this, "table-view", "UITableView");
        this.tableView.dataSource = this;
        this.tableView.delegate = this;
    }

    // The list controller creates a new instance on every ADD, so the selection starts clean by construction
    // and survives pushing/popping the lines screen.
    viewWillAppear(animated?:boolean){
        super.viewWillAppear(animated);
        this.fetchedResultsController = null;
        this.tableView.reloadData();
    }

    // Section 0: the "include minimum stock" switch. Section 1: the pending warehouse orders.
    numberOfSections(tableView:UITableView){
        return 2;
    }

    numberOfRowsInSection(tableView:UITableView, section:number){
        if (section == 0) return 1;
        let orders = this.fetchedResultsController.fetchedObjects.length;
        return orders == 0 ? 1 : orders;
    }

    titleForHeaderInSection(tableView:UITableView, section:number){
        return section == 0 ? null : MIOLocalizeString("PENDING_WAREHOUSE_ORDERS", "PENDING WAREHOUSE ORDERS");
    }

    cellAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath){
        if (indexPath.section == 0) {
            let cell = tableView.dequeueReusableCellWithIdentifier("OptionCell") as BuyOrderWarehouseOrderOptionCell;
            cell.changeTarget = this;
            cell.changeAction = this.includeMinStockDidChange;
            cell.checked = this.includeMinStock;
            return cell;
        }

        if (this.fetchedResultsController.fetchedObjects.length == 0) {
            return tableView.dequeueReusableCellWithIdentifier("EmptyCell") as UITableViewCell;
        }

        let cell = tableView.dequeueReusableCellWithIdentifier("WarehouseOrderCell") as BuyOrderWarehouseOrderCell;
        let order = this.orderAtIndexPath(indexPath);
        cell.item = order;
        cell.selectionTarget = this;
        cell.selectionAction = this.toggleOrderSelection;
        cell.checked = this.selectedOrders.containsObject(order);
        return cell;
    }

    // Tapping the row (or its disclosure) shows the lines of the order; selection is only through the circle
    didSelectCellAtIndexPath(tableView:UITableView, indexPath:MIOIndexPath){
        if (indexPath.section == 0) return;
        if (this.fetchedResultsController.fetchedObjects.length == 0) return;

        let order = this.orderAtIndexPath(indexPath);
        let vc = BuyOrderWarehouseOrderLinesViewController.newInstance();
        vc.order = order;
        if (this.navigationController != null) this.navigationController.pushViewController(vc, true);
        else this.presentViewController(vc, true);
    }

    private orderAtIndexPath(indexPath:MIOIndexPath) : WarehouseOrder {
        return this.fetchedResultsController.objectAtIndexPath(MIOIndexPath.indexForRowInSection(indexPath.row, 0)) as WarehouseOrder;
    }

    private includeMinStockDidChange(value:boolean){
        this.includeMinStock = value;
    }

    private toggleOrderSelection(order:WarehouseOrder){
        if (this.selectedOrders.containsObject(order)) this.selectedOrders.removeObject(order);
        else this.selectedOrders.addObject(order);

        let row = this.fetchedResultsController.fetchedObjects.indexOf(order);
        if (row < 0) return;
        let cell = this.tableView.cellAtIndexPath(MIOIndexPath.indexForRowInSection(row, 1)) as BuyOrderWarehouseOrderCell;
        if (cell != null) cell.checked = this.selectedOrders.containsObject(order);
    }

    private _fetchedResultsController:MIOFetchedResultsController = null;
    set fetchedResultsController(value){
        if (value == null && this._fetchedResultsController != null) this._fetchedResultsController.delegate = null;
        this._fetchedResultsController = value;
    }

    // Every warehouse order of the venue not processed yet (the processed ones already became deliveries)
    // and not already taken by a buy order (parent is the buy order that purchased it)
    get fetchedResultsController(){
        if (this._fetchedResultsController != null) return this._fetchedResultsController;

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;

        let sortDescriptors = [MIOSortDescriptor.sortDescriptorWithKey("stockDate", false)];
        let predicateFormat = "status != " + StockNoteStatus.Processed + " AND parent == null AND placeID == '" + ad.selectedIdentifier + "'";

        let fetchRequest = DBHelper.listFetchRequestWithEntityName("WarehouseOrder", sortDescriptors, predicateFormat);
        fetchRequest.relationshipKeyPathsForPrefetching = ["originWarehouse", "destinationWarehouse"];

        let fetchedResultsController = new MIOFetchedResultsController();
        fetchedResultsController.initWithFetchRequest(fetchRequest, ad.managedObjectContext, null);
        fetchedResultsController.delegate = this;
        fetchedResultsController.performFetch();

        this._fetchedResultsController = fetchedResultsController;
        return this._fetchedResultsController;
    }

    controllerDidChangeContent(controller:MIOFetchedResultsController){
        this.tableView.reloadData();
    }

    private doneAction(){
        let orders = this.selectedOrders.allObjects as WarehouseOrder[];
        if (orders.length == 0 && this.includeMinStock == false) {
            AppHelper.showErrorMessage(this, MIOLocalizeString("FROM_WAREHOUSE_ORDERS", "FROM WAREHOUSE ORDERS"), MIOLocalizeString("SELECT_WAREHOUSE_ORDERS_OR_MINIMUM_STOCK", "Select at least one warehouse order or include the minimum stock products"));
            return;
        }

        let target = this.completionTarget;
        let block = this.completionBlock;
        let includeMinStock = this.includeMinStock;
        this.dismissViewController(true);
        if (block != null) block.call(target, orders, includeMinStock);
    }
}
